import React from 'react';
import styled, {css} from 'styled-components';
import {DropType} from '../../../../grpc_client/grips/protos/shared/drop';
import {DropsBannerProps} from '../../../../pages/Drops/types';
import {
  DROP_PATH_TO_TYPE_MAP,
  DROP_TYPE_TO_PATH_MAP,
} from '../../../../pages/Drops/utils/constants';
import {cdnImageUrl} from '../../../../utils/cdnImageUrl';
import useDropTypeFromPath from '../../../../utils/hooks/useDropTypeFromPath';
import Icon, {IconSize} from '../../../Icon/Icon';
import {GenericIconName} from '../../../Icon/IconName';
import {dropsYellow, primaryColor} from '../../../constants/colors';
import {mediaMinTablet} from '../../../constants/customMedia';
import {
  fontSize16,
  fontSize20,
  fontSize32,
  fontSize48,
  fwHeavy,
  fwMediumBold,
  lhM,
} from '../../../constants/typography';

const Container = styled.section<{
  backgroundImage?: string;
  dropType: DropType;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 310px;
  background-image: url(${({backgroundImage}) => backgroundImage});
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  color: ${({dropType}) =>
    dropType === DropType.DROP_TYPE_AY_LABEL ? primaryColor : dropsYellow};

  @media ${mediaMinTablet} {
    min-height: 410px;
  }
  position: relative;
`;
const Gradient = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  background-image: linear-gradient(#000, rgba(0, 0, 0, 0.1) 0%);
  top: 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 50px 20px;

  &:before,
  &:after {
    content: '';
    display: block;
  }
`;

const IconWrapper = styled.div`
  height: 50px;
  width: 50px;
  position: relative;

  @media ${mediaMinTablet} {
    height: 64px;
    width: 64px;
  }
`;

const StyledIcon = styled(Icon)`
  height: 100%;
  width: 100%;
`;

const Title = styled.div<{isPremiumDropPage: boolean}>`
  ${fontSize32};
  ${fwHeavy};
  line-height: ${lhM};
  color: currentColor;
  text-align: center;

  ${({isPremiumDropPage}) =>
    !isPremiumDropPage &&
    css`
      margin-top: 20px;
    `};

  @media ${mediaMinTablet} {
    ${fontSize48};
  }
`;

const USPList = styled.ul`
  padding: 0;
  margin-top: 17px;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: currentColor;
  width: 90%;
  text-align: center;
  list-style: none;

  @media ${mediaMinTablet} {
    margin-top: 34px;
  }
`;

const USP = styled.li`
  ${fontSize16};
  ${fwMediumBold};
  line-height: ${lhM};
  margin-top: 6px;

  @media ${mediaMinTablet} {
    ${fontSize20};
    letter-spacing: 0.15px;
  }
`;

const DropBanner: React.FC<DropsBannerProps> = ({imageHash, title, usps}) => {
  const dropType = useDropTypeFromPath();
  const isPremiumDropPage =
    dropType === DROP_TYPE_TO_PATH_MAP[DropType.DROP_TYPE_AY_PREMIUM];

  return (
    <Container
      {...(imageHash && {
        backgroundImage: cdnImageUrl(imageHash),
      })}
      dropType={DROP_PATH_TO_TYPE_MAP[dropType]}
    >
      <Gradient />
      <ContentWrapper>
        {!isPremiumDropPage && (
          <IconWrapper>
            <StyledIcon
              asset={GenericIconName.IcDropTimer}
              size={IconSize.Custom}
            />
          </IconWrapper>
        )}

        <Title isPremiumDropPage={isPremiumDropPage}>{title?.text}</Title>

        {usps && usps.length > 0 && (
          <USPList>
            {usps?.map((usp, i: number) => (
              <USP key={i}>{usp?.text}</USP>
            ))}
          </USPList>
        )}
      </ContentWrapper>
    </Container>
  );
};

export default DropBanner;
